'use client';

import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import styles from './SearchTrigger.module.css';
import TypewriterText from './TypewriterText';

interface Props {
  onClick: () => void;
  expanded?: boolean;
  controls?: string;
}

/* Static-looking search bar that, on click/focus, opens the
   full-screen search overlay. Looks like an input but is actually a
   button — keyboard activates with Enter/Space. The leading icon
   carries the "search" affordance on its own; no redundant label. */
const SearchTrigger = ({ onClick, expanded, controls }: Props) => {
  const t = useTranslations('science');

  /* Example queries cycled by the typewriter placeholder. Mix of topics so the
     prompt feels like a real "search the science" surface, not a fixed label. */
  const phrases = useMemo(
    () => [
      t('science.search.phrase1'),
      t('science.search.phrase2'),
      t('science.search.phrase3'),
      t('science.search.phrase4'),
      t('science.search.phrase5'),
      t('science.search.phrase6'),
    ],
    [t],
  );

  return (
    <button
      type="button"
      className={styles.trigger}
      onClick={onClick}
      aria-label={t('science.search.triggerLabel')}
      aria-expanded={expanded}
      aria-controls={controls}
    >
      <svg
        className={styles.icon}
        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <circle cx="11" cy="11" r="7" />
        <path d="m21 21-4.3-4.3" />
      </svg>
      <span className={styles.phrase} aria-hidden="true">
        <TypewriterText phrases={phrases} />
      </span>
      <span className={styles.hint} aria-hidden="true">
        ⌘K
      </span>
    </button>
  );
};

export default SearchTrigger;
